import { TimesheetStatus } from '@sdk/timesheets/api/apiTypes';
import context from '../context';
import utils from '../utils';
import { TIMESHEET_ENTRY_TYPES } from '../constants';

export function isClockIn(timesheet = {}) {
  return timesheet.type === TIMESHEET_ENTRY_TYPES.CLOCK_IN;
}

export function isClockOut(timesheet = {}) {
  return (
    timesheet.type === TIMESHEET_ENTRY_TYPES.CLOCK_OUT ||
    timesheet.type === TIMESHEET_ENTRY_TYPES.AUTO_CLOCK_OUT
  );
}

export function isBreakStart(timesheet = {}) {
  return timesheet.type === TIMESHEET_ENTRY_TYPES.BREAK_START;
}

export function isBreakEnd(timesheet = {}) {
  return timesheet.type === TIMESHEET_ENTRY_TYPES.BREAK_END;
}

export function isClockOutCorrect(boundaryEnd, boundaryStart) {
  return Boolean(!boundaryEnd || boundaryEnd >= boundaryStart);
}

export function isBreakEndRequired(boundaryEnd, sortedBreaks, breakObject) {
  return Boolean(breakObject !== sortedBreaks[sortedBreaks.length - 1] || boundaryEnd);
}

export function canEditTimesheet(event) {
  if (!event.user || !event.user.timeclockEnabled) {
    return false;
  }

  if (event.timesheetStatus === TimesheetStatus.approved) {
    return Boolean(context.me.canActOnLocation(event.location));
  }

  if (!context.me.isManager) {
    return Boolean(
      event.user.id === context.me.id && context.orgSettings.usersCanEditTheirTimesheets
    );
  }

  return Boolean(event.user.id === context.me.id || context.me.canActOnLocation(event.location));
}

export function areBreaksCorrect(entry) {
  const activeBreaks = entry.breaks.filter((b) => !b.deleted);
  let beyondStart = false;
  let beyondEnd = false;
  let overlap = false;
  let invalidBreak = false;
  let negativeBreak = false;

  const sortedBreaks = [...activeBreaks].sort((a, b) => a.breakStart > b.breakStart);
  activeBreaks.forEach((breakObject) => {
    invalidBreak =
      invalidBreak ||
      !breakObject.breakStart ||
      (isBreakEndRequired(entry.boundaryEnd, sortedBreaks, breakObject) && !breakObject.breakEnd);
    beyondStart =
      beyondStart || (entry.boundaryStart && breakObject.breakStart < entry.boundaryStart);
    beyondEnd = beyondEnd || (entry.boundaryEnd && breakObject.breakEnd > entry.boundaryEnd);
    negativeBreak =
      negativeBreak ||
      (breakObject.breakStart &&
        breakObject.breakEnd &&
        breakObject.breakStart > breakObject.breakEnd);
    overlap = overlap || _doesBreakOverlapWithOthers(activeBreaks, breakObject);
  });

  // if it's invalid it will show itself as erroneous one itself - no need to bloat the user with this as well
  return invalidBreak || (!beyondStart && !beyondEnd && !overlap && !negativeBreak);
}

function _doesBreakOverlapWithOthers(breaks, breakObject) {
  return breaks.some(
    (otherBreak) =>
      !(
        breakObject === otherBreak ||
        (!breakObject.breakEnd &&
          (breakObject.breakStart <= otherBreak.breakStart ||
            breakObject.breakStart >= otherBreak.breakEnd)) ||
        breakObject.breakEnd <= otherBreak.breakStart ||
        breakObject.breakStart >= otherBreak.breakEnd
      )
  );
}

export function getRosterStatusTemplate({
  statusDict,
  event,
  noShowsEnabled,
  sickCalloutEnabled,
  hasToastIntegration,
  clockInEnabled,
}) {
  if (noShowsEnabled && event.type === 'no_show') {
    return statusDict.noShow;
  }

  if (sickCalloutEnabled && event.type === 'sick_callout') {
    return statusDict.sickCallout;
  }

  if (hasToastIntegration || clockInEnabled) {
    const clockStatus = _getClockInStatusTemplate(statusDict, event);
    if (clockStatus) {
      return clockStatus;
    }
  }

  return statusDict.noShift;
}

function _getClockInStatusTemplate(statusDict, event) {
  if (event?.clockInStatus?.clockedOut) {
    return statusDict.clockedOut;
  }

  if (event?.clockInStatus?.onBreak) {
    return statusDict.onBreak;
  }

  if (event?.clockInStatus?.clockedIn) {
    return statusDict.clockedIn;
  }

  if (utils.getRealDate(event.dtstartLocal) < new Date()) {
    return statusDict.late;
  }
}
